import { unset } from "lodash";
import { FC } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Create,
  CreateProps,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
} from "react-admin";
import CrudBreadcrumb from "../../components/CrudBreadcrumb";
import SelectOrTextInput from "../../components/fields/SelectOrTextInput";
import HospitalRoomInput from "../../components/inputs/HospitalRoomInput";
import bookings from "./";
import { MultipleSurgeryDates } from "./MultipleSurgeryDates";
import useStyles from "./shared/useStyles";

const BookingCreateToolbar: FC<any> = (props) => (
  <Toolbar {...props}>
    <SaveButton
      // disabled={props.pristine}
      transform={(data) => {
        // unset the hospital, or procedure name if they are null
        if (!data.procedure?.name) {
          unset(data, "procedure");
        } else {
          unset(data, "procedure_id");
        }

        if (!data.surgeon?.full_name) {
          unset(data, "surgeon");
        } else {
          unset(data, "surgeon_id");
        }

        // Convert date object to array and filter out empty values
        let processedDate;

        if (
          data.date &&
          typeof data.date === "object" &&
          !Array.isArray(data.date)
        ) {
          // Convert object to array (e.g., {0: "date1", 1: "date2"} => ["date1", "date2"])
          const dateValues = Object.values(data.date).filter(
            (d) => d !== null && d !== undefined && d !== "",
          );
          processedDate = dateValues.length > 0 ? dateValues : undefined;
        } else if (Array.isArray(data.date)) {
          // If already an array, filter out empty values
          const filteredDates = data.date.filter(
            (d: string | null | undefined) =>
              d !== null && d !== undefined && d !== "",
          );
          processedDate = filteredDates.length > 0 ? filteredDates : undefined;
        } else if (data.date) {
          // If it's a single date, convert to array
          processedDate = [data.date];
        }

        return {
          ...data,
          date: processedDate,
          items: [
            ...(data.implantitems ?? []),
            ...(data.instrumentitems ?? []),
          ],
        };
      }}
    />
  </Toolbar>
);

// const CustomDateComp = () => {
//   const isUD = useField("is_eot").input.value;

//   return (
//     <DateTimeInput
//       source="date"
//       disabled={isUD}
//       initialValue={isUD ? new Date(new Date().setUTCHours(0, 0, 0, 0)) : null}
//       parse={(value: any) => convertJsToSql(value)}
//     />
//   );
// };

export const BookingCreate: FC<CreateProps> = (props) => {
  const classes = useStyles();
  const isSR = process.env.REACT_APP_COMPANY_CODE === "SNR";

  // // #region initialise surgeons
  // const { data: surgeons }: { data?: Surgeon[] } = useQueryWithStore({
  //   type: "getList",
  //   resource: "surgeons",
  //   payload: {
  //     pagination: { perPage: 1000 },
  //     sort: { field: "name", order: "asc" },
  //     filter: { active: true },
  //   },
  // });
  // const [surgeonsList, setSurgeonsList] = useState<ChoiceProps[]>();
  // useEffect(() => {
  //   if (!surgeons) return;

  //   setSurgeonsList(
  //     surgeons.map((surgeon) => ({
  //       id: `${surgeon.id}`,
  //       name: surgeon.full_name,
  //     }))
  //   );
  // }, [surgeons]);
  // // #endregion

  // // #region initialise consultants
  // const { data: consultants }: { data?: Consultant[] } = useQueryWithStore({
  //   type: "getList",
  //   resource: "surgeons",
  //   payload: {
  //     pagination: { perPage: 1000 },
  //     sort: { field: "name", order: "asc" },
  //     filter: { active: true },
  //   },
  // });
  // const [consultantList, setConsultantList] = useState<ChoiceProps[]>();
  // useEffect(() => {
  //   if (!consultants) return;

  //   setConsultantList(
  //     consultants.map((item) => ({
  //       id: `${item.id}`,
  //       name: item.full_name,
  //     }))
  //   );
  // }, [consultants]);
  // // #endregion

  return (
    <>
      <CrudBreadcrumb
        resourceName={bookings.name}
        mode="create"
        resourceId={""}
      />
      <Create {...props} className={classes.root}>
        <SimpleForm
          toolbar={<BookingCreateToolbar />}
          initialValues={{ date: [""] }}
        >
          <BooleanInput
            source="is_eot"
            label={
              process.env.REACT_APP_COMPANY_CODE === "STMY"
                ? "Is UD?"
                : "Is EOT?"
            }
          />
          <ReferenceInput
            source="consultant_id"
            reference="consultants"
            sort={{ field: "full_name", order: "asc" }}
          >
            <AutocompleteInput optionText="full_name" />
          </ReferenceInput>
          <MultipleSurgeryDates source="date" isCreate={true} />
          <SelectOrTextInput
            source="surgeon_id"
            textSource="surgeon.full_name"
            label="Surgeon"
            placeholder="Select surgeon or enter new surgeon name"
            choices={{
              resource: "surgeons",
              optionString: "full_name",
              optionValue: "id",
              filter: { status: "active" },
            }}
          />
          <SelectOrTextInput
            source="procedure_id"
            textSource="procedure.name"
            label={
              process.env.REACT_APP_COMPANY_CODE === "STMY"
                ? "Order Type"
                : "Procedure"
            }
            placeholder={
              process.env.REACT_APP_COMPANY_CODE === "STMY"
                ? "Select order type or enter new order type name"
                : "Select procedure or enter new procedure name"
            }
            choices={{
              resource: "procedures",
              optionString: "name",
              optionValue: "id",
            }}
          />
          <HospitalRoomInput source="hospital_room_id" />
          <TextInput source="remarks" />
          <h2>{isSR ? "Devices" : "Instruments"}</h2>
          <ArrayInput
            source="instrumentitems"
            label={isSR ? "Device Items" : "Instrument Items"}
          >
            <SimpleFormIterator className={classes.instrument}>
              <ReferenceInput
                label={isSR ? "Device Name" : "Instrument Name"}
                source="product_id"
                reference="products"
                sort={{ field: "name", order: "ASC" }}
                filter={{ type: "instrument" }}
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
              <NumberInput source="quantity" label="Quantity" min="1" />
            </SimpleFormIterator>
          </ArrayInput>
          <h2>{isSR ? "Consumables" : "Implants"}</h2>
          <ArrayInput
            source="implantitems"
            label={isSR ? "Consumable Items" : "Implant Items"}
          >
            <SimpleFormIterator className={classes.instrument}>
              <ReferenceInput
                label={isSR ? "Consumable Name" : "Implant Name"}
                source="product_id"
                reference="products"
                sort={{ field: "name", order: "ASC" }}
                filter={{ type: "implant" }}
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
              <NumberInput source="quantity" label="Quantity" min="1" />
            </SimpleFormIterator>
          </ArrayInput>
          <h2>Additional Items</h2>
          <TextInput
            multiline
            source="additional_items"
            placeholder={
              isSR
                ? "Enter the additional items that are not in the Device and Consumable lists"
                : "Enter the additional items that are not in the Instrument and Implant lists"
            }
          />
        </SimpleForm>
      </Create>
    </>
  );
};
